<template>
    <div
        class="gap-4 border border-solid border-b-0 border-x-0 border-slate-200 bg-slate-50 first:border-white odd:bg-slate-100 last:rounded-b-xl"
    >
        <div
            class="grid grid-cols-[1fr_1fr_minmax(100px,auto)] items-center transition min-h-[48px] px-8 py-2"
            :class="[
                {
                    'opacity-0': props.loading,
                },
            ]"
        >
            <div
                class="text-xs"
                :class="{
                    'font-bold': props.metric === props.activeMetric,
                }"
            >
                {{ metricTitle }}:
            </div>
            <div class="font-semibold text-xs text-center">
                <vx-popover
                    v-if="props.resourceMetric"
                    arrow
                    hover
                    placement="top"
                    class="cursor-pointer"
                    @click="copyText(copyValue)"
                >
                    {{ formattedValue }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(copyValue)">
                            {{ popoverValue }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </div>
            <div class="flex justify-end">
                <vx-popover
                    v-if="showGoalColumn"
                    hover
                    arrow
                    :openDelay="75"
                    placement="top"
                    class="relative z-10 flex h-full [&>.vx-popover]:transition-opacity [&>.vx-popover]:duration-75"
                >
                    <vx-badge
                        size="xs"
                        class="flex!"
                        :class="{
                            'text-sky-900': isAboveTarget,
                            'text-slate-900': !isAboveTarget,
                        }"
                        :color="isAboveTarget ? 'primary' : 'slate'"
                    >
                        {{ formattedComparisonPercent }}
                        <font-awesome-icon
                            widthAuto
                            :icon="faPercent"
                            :class="{
                                'text-sky-500': isAboveTarget,
                                'text-slate-500': !isAboveTarget,
                            }"
                        ></font-awesome-icon>
                    </vx-badge>

                    <template v-slot:content>
                        <span
                            class="flex flex-row items-center justify-center text-center text-sm font-normal text-slate-800"
                        >
                            <span>Goal&nbsp;</span>
                            <span class="font-bold">{{ formatPercentage(props.target, true) }}</span>
                        </span>
                    </template>
                </vx-popover>
            </div>
        </div>
    </div>
</template>

<script setup>
import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxPopover, vxFormatCentsAsDollars } from '@voxie/frontend-components';
import { metricLabel, isReversedMetric, metricsOptions } from '~/utils/analytics';
import { formatCount, formatPercentage } from '~/components/filters';
import { computed } from 'vue';
import { currencyCents, formatFractionToPercent } from '~/utils/helpers';
import { useCopyText } from '~/composables/useCopyText';

const props = defineProps({
    metric: {
        type: String,
        required: true,
        validator: (value) => metricsOptions.some((metric) => metric.value === value),
    },
    target: {
        type: Number,
        required: false,
    },
    activeMetric: {
        type: String,
        required: false,
        validator: (value) => metricsOptions.some((metric) => metric.value === value),
    },
    resourceMetric: {
        type: Number,
        required: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const { copyText } = useCopyText();

const metricTitle = computed(() => metricLabel(props.metric));
const formattedComparisonPercent = computed(() => {
    if (!props.target) {
        return;
    }
    let comparisonPercent = (props.resourceMetric - props.target) / props.target;
    const multiplier = isReversedMetric(props.metric) ? -1 : 1;
    comparisonPercent = (comparisonPercent * multiplier).toFixed(2);

    return `${comparisonPercent > 0 ? '+' : ''}${formatPercentage(comparisonPercent, false)}`;
});
const isAboveTarget = computed(() => {
    if (!props.target) {
        return;
    }
    return isReversedMetric(props.metric) ? props.resourceMetric <= props.target : props.resourceMetric >= props.target;
});

const usesPercentage = computed(() => metricsOptions.find((metric) => metric.value === props.metric)?.uses_percentage);
const usesCurrency = computed(() => metricsOptions.find((metric) => metric.value === props.metric)?.uses_currency);

const showGoalColumn = computed(() => props.target && props.resourceMetric != null);

const formattedValue = computed(() => {
    if (usesCurrency.value) {
        return vxFormatCentsAsDollars(props.resourceMetric, true);
    }
    if (usesPercentage.value) {
        return formatPercentage(props.resourceMetric, true);
    }
    return formatCount(props.resourceMetric);
});

const copyValue = computed(() => {
    if (usesCurrency.value) {
        return currencyCents(props.resourceMetric);
    }
    if (usesPercentage.value) {
        return formatFractionToPercent(props.resourceMetric, true);
    }
    return props.resourceMetric;
});

const popoverValue = computed(() => {
    if (usesCurrency.value) {
        return currencyCents(props.resourceMetric);
    }
    if (usesPercentage.value) {
        return formatFractionToPercent(props.resourceMetric, true);
    }
    return Intl.NumberFormat('en-US').format(props.resourceMetric);
});
</script>
